import * as THREE from 'three';
import type { ResourceModel } from '../../../gameplay/types';
import { ModelBuilder, Part, type PartOpts } from './builder';

const T = (color: number, part: Part = Part.Static, pivot?: [number, number, number]): PartOpts => ({ color, part, pivot });

/** Several variants per resource model so forests don't look cloned. */
export function buildResourceGeometry(model: ResourceModel, variant: number): THREE.BufferGeometry {
  const b = new ModelBuilder();
  const v = variant;
  const sway: [number, number, number] = [0, 0, 0];
  switch (model) {
    case 'tree': {
      const h = 0.9 + v * 0.18;
      // Kept light: forests are the bulk of the triangle count on big maps.
      b.cyl(0.07, 0.1, h, 5, T(0x5a3e26), 0, h / 2, 0);
      const greens = [0x3e6a2a, 0x4a7a30, 0x36602a, 0x56843a];
      const blobs = [[0, h + 0.25, 0, 0.44], [0.24, h + 0.05, 0.1, 0.34], [-0.22, h + 0.1, -0.12, 0.36]];
      for (let i = 0; i < blobs.length; i++) {
        const [x, y, z, r] = blobs[i];
        b.sphere(r, T(greens[(i + v) % greens.length], Part.Sway, sway), x, y, z, 1, 0.85, 1, i === 0 ? 1 : 0);
      }
      break;
    }
    case 'pine': {
      const h = 0.4 + v * 0.1;
      b.cyl(0.05, 0.08, h + 0.4, 5, T(0x4a3420), 0, (h + 0.4) / 2, 0);
      const c = [0x24482a, 0x2a5230, 0x1e4026][v % 3];
      for (let i = 0; i < 3; i++) {
        const r = 0.44 - i * 0.1;
        b.cone(r, 0.65, 7, T(c, Part.Sway, sway), 0, h + 0.2 + i * 0.4, 0);
      }
      break;
    }
    case 'palm': {
      let x = 0;
      let y = 0;
      for (let i = 0; i < 5; i++) {
        b.cyl(0.05, 0.065, 0.32, 6, T(0x8a6a42, Part.Sway, sway), x, y + 0.16, 0, 0, 0, -0.08 * i);
        x += 0.025 * i;
        y += 0.3;
      }
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2 + v;
        b.box(0.6, 0.02, 0.14, T(0x4a8a3a, Part.Sway, sway), x + Math.cos(a) * 0.28, y - 0.05, Math.sin(a) * 0.28, 0, -a, -0.4);
      }
      break;
    }
    case 'berry':
      b.sphere(0.32, T(0x3a6a2a), 0, 0.24, 0, 1.1, 0.75, 1.05, 1);
      b.sphere(0.22, T(0x45782e), 0.12, 0.36, 0.05, 1, 0.8, 1, 1);
      for (let i = 0; i < 14; i++) {
        const a = i * 2.39996;
        const r = 0.26 + (i % 3) * 0.03;
        b.sphere(0.04, T(i % 4 === 0 ? 0x6a2a6a : 0xc02a3a), Math.cos(a) * r, 0.2 + (i % 5) * 0.06, Math.sin(a) * r, 1, 1, 1, 0);
      }
      break;
    case 'gold':
    case 'stone': {
      const rock = model === 'gold' ? 0x6a625a : 0x8a8680;
      const nug = model === 'gold' ? 0xe8c040 : 0xb8b4ac;
      b.add(new THREE.DodecahedronGeometry(0.36, 0), T(rock), 0, 0.18, 0, v, v * 2, 0, 1.2, 0.7, 1.1);
      b.add(new THREE.DodecahedronGeometry(0.22, 0), T(rock), 0.28, 0.12, 0.15, v * 3, 0, v, 1, 0.8, 1);
      for (let i = 0; i < 6; i++) {
        const a = i * 1.7 + v;
        b.add(new THREE.OctahedronGeometry(0.08, 0), T(nug), Math.cos(a) * 0.28, 0.12 + (i % 3) * 0.1, Math.sin(a) * 0.28, a, a, 0);
      }
      break;
    }
    case 'deer': {
      const c = 0x9a6a3a;
      b.box(0.14, 0.14, 0.38, T(c), 0, 0.32, 0);
      b.box(0.06, 0.18, 0.08, T(c, Part.Sway, [0, 0.36, 0.17]), 0, 0.44, 0.2, 0.5, 0, 0);
      b.box(0.07, 0.07, 0.14, T(c, Part.Sway, [0, 0.36, 0.17]), 0, 0.52, 0.28);
      b.box(0.02, 0.12, 0.02, T(0xd8c8a8, Part.Sway, [0, 0.36, 0.17]), 0.03, 0.62, 0.24, 0, 0, 0.4);
      b.box(0.02, 0.12, 0.02, T(0xd8c8a8, Part.Sway, [0, 0.36, 0.17]), -0.03, 0.62, 0.24, 0, 0, -0.4);
      for (const [x, z] of [[-0.05, 0.13], [0.05, 0.13], [-0.05, -0.13], [0.05, -0.13]]) b.box(0.035, 0.26, 0.035, T(c), x, 0.13, z);
      break;
    }
    case 'fish': {
      // A shoal: several fish just under the surface plus a few ripples.
      for (let i = 0; i < 5; i++) {
        const a = i * 1.3 + v;
        const x = Math.cos(a) * 0.25;
        const z = Math.sin(a) * 0.25;
        b.sphere(0.06, T(0x7a9aaa, Part.Sway, [x, -0.15, z]), x, -0.15, z, 0.6, 0.4, 1.6, 0);
      }
      b.add(new THREE.RingGeometry(0.25, 0.3, 16).rotateX(-Math.PI / 2), T(0xd8ecf4), 0, -0.05, 0);
      b.add(new THREE.RingGeometry(0.45, 0.49, 20).rotateX(-Math.PI / 2), T(0xc8e0ea), 0.1, -0.05, -0.05);
      break;
    }
    case 'sheep': {
      b.sphere(0.17, T(0xf0ece0), 0, 0.26, 0, 1, 0.85, 1.3, 1);
      b.box(0.08, 0.08, 0.1, T(0x2a2622, Part.Sway, [0, 0.28, 0.15]), 0, 0.26, 0.22);
      for (const [x, z] of [[-0.06, 0.1], [0.06, 0.1], [-0.06, -0.1], [0.06, -0.1]]) b.box(0.03, 0.14, 0.03, T(0x2a2622), x, 0.07, z);
      break;
    }
  }
  return b.build();
}

export function resourceVariants(model: ResourceModel): number {
  return model === 'tree' || model === 'pine' ? 3 : model === 'palm' ? 2 : 1;
}

/** Ground grass tufts for decoration. */
export function buildGrassTuft(): THREE.BufferGeometry {
  const b = new ModelBuilder();
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    b.box(0.02, 0.16, 0.05, T(i % 2 ? 0x6a8a3a : 0x5a7a30, Part.Sway, [0, 0, 0]), Math.cos(a) * 0.05, 0.08, Math.sin(a) * 0.05, Math.sin(a) * 0.3, a, Math.cos(a) * 0.3);
  }
  return b.build();
}

/** Projectiles are deliberately oversized so volleys and shots read at normal camera zoom. */
export function buildProjectile(kind: string): THREE.BufferGeometry {
  const b = new ModelBuilder();
  switch (kind) {
    case 'arrow':
    case 'bolt': {
      const len = kind === 'arrow' ? 0.7 : 0.5;
      b.box(0.04, 0.04, len, T(0x8a6238));
      b.cone(0.06, 0.14, 4, T(0xe0e0e8), 0, 0, len / 2 + 0.06, Math.PI / 2, 0, 0);
      // Fletching.
      b.box(0.12, 0.012, 0.14, T(0xf4f0e6), 0, 0, -len / 2 + 0.07);
      b.box(0.012, 0.12, 0.14, T(0xf4f0e6), 0, 0, -len / 2 + 0.07);
      break;
    }
    case 'stone':
      b.add(new THREE.DodecahedronGeometry(0.24, 0), T(0x8a847a));
      break;
    case 'ball':
    case 'shell':
      b.sphere(0.15, T(0x18181c), 0, 0, 0, 1, 1, 1, 1);
      break;
    default:
      // Musket balls: a bright tracer streak.
      b.box(0.05, 0.05, 0.45, T(0xffe8a0));
  }
  return b.build();
}
